import { useMemo, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger, PageHeader, DatePicker, MultiSelect, Button, Modal } from '@atrvd/saas-ui'
import { KpiCards, ManualTransactionsTable, ManualTransactionFormDialog, ManualTransactionBatchFormDialog } from '@/components/banking'
import { FileImportSection } from '@/components/import/FileImportSection'
import { transactionImportConfig } from '@/components/import/import-configs'
import {
  useBankingKpi,
  useFacilities,
  useManualTransactions,
  useManualTransactionCount,
  useDeleteManualTransaction,
} from '@/hooks/useBanking'
import { toLocalDateString, safeParseDate } from '@/lib/date-utils'
import type { TransactionsImportTab, ManualTransactionFilters } from '@/types/banking'
import type { TenantTransaction } from '@/lib/db-tenant'

/**
 * Transactions Import page
 * Displays KPI cards, date/facility filters, and tabbed content
 * including the manual transactions list with edit/delete actions.
 */
export function TransactionsImportPage() {
  const [activeTab, setActiveTab] = useState<TransactionsImportTab>('transactions-manual-input')
  const [page, setPage] = useState(1)
  const pageSize = 10

  // Filters: Date range + Facility (per AC)
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [facilityIds, setFacilityIds] = useState<string[]>([])

  const manualFiltersBase: ManualTransactionFilters = useMemo(() => ({
    startDate,
    endDate,
    facilityIds: facilityIds.length ? facilityIds : undefined,
  }), [startDate, endDate, facilityIds])

  const manualFiltersWithPagination = useMemo(() => ({
    ...manualFiltersBase,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  }), [manualFiltersBase, page, pageSize])

  // KPI filters — same date/facility scope
  const kpiFilters = useMemo(() => ({
    startDate,
    endDate,
    facilityIds: facilityIds.length ? facilityIds : undefined,
  }), [startDate, endDate, facilityIds])

  const [isEditFormOpen, setIsEditFormOpen] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<TenantTransaction | undefined>()
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  /** State for delete confirmation dialog */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [txToDelete, setTxToDelete] = useState<TenantTransaction | null>(null)

  const deleteTransaction = useDeleteManualTransaction()

  // Fetch data
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(kpiFilters)
  const { data: facilitiesData } = useFacilities()
  const facilities = facilitiesData ?? []
  const { data: manualTransactions, isLoading: manualLoading } = useManualTransactions(manualFiltersWithPagination)
  const { data: manualTotalCount = 0 } = useManualTransactionCount(manualFiltersBase)

  const isLoading = kpiLoading

  /** Clear all filters and reset pagination */
  const clearManualFilters = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setFacilityIds([])
    setPage(1)
  }

  /** Open edit dialog with the selected transaction */
  const handleEdit = (tx: TenantTransaction) => {
    setEditingTransaction(tx)
    setIsEditFormOpen(true)
  }

  /** Open the delete confirmation dialog */
  const handleDelete = (tx: TenantTransaction) => {
    setTxToDelete(tx)
    setDeleteConfirmOpen(true)
  }

  /** Confirm and execute transaction deletion */
  const confirmDelete = async () => {
    if (!txToDelete) return

    try {
      await deleteTransaction.mutateAsync(txToDelete.id)
      toast.success('Transaction deleted')
      setDeleteConfirmOpen(false)
      setTxToDelete(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete transaction'
      toast.error(message)
    }
  }

  /** Close edit dialog and clear the editing transaction state */
  const handleEditFormClose = () => {
    setIsEditFormOpen(false)
    setEditingTransaction(undefined)
  }

  if (isLoading && !kpi) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Transactions Import"
        description="Import and manage banking transactions"
      />

      {/* Filters: Date range + Facility */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted-foreground">From</label>
          <DatePicker
            date={safeParseDate(startDate)}
            onDateChange={(date) => {
              if (!date) { setStartDate(undefined); return }
              const dateStr = toLocalDateString(date)
              if (endDate && dateStr > endDate) setEndDate(dateStr)
              setStartDate(dateStr)
              setPage(1)
            }}
            placeholder="Start date"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted-foreground">To</label>
          <DatePicker
            date={safeParseDate(endDate)}
            onDateChange={(date) => {
              if (!date) { setEndDate(undefined); return }
              const dateStr = toLocalDateString(date)
              if (startDate && dateStr < startDate) setStartDate(dateStr)
              setEndDate(dateStr)
              setPage(1)
            }}
            placeholder="End date"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-muted-foreground">Facility</label>
          <MultiSelect
            options={facilities.map((f) => ({ value: f.id, label: f.name }))}
            value={facilityIds}
            onChange={(value) => { setFacilityIds(value); setPage(1) }}
            placeholder="All Facilities"
            className="w-48"
            searchable
            searchPlaceholder="Search facilities..."
          />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Add Transaction */}
        <Button
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={() => setIsCreateFormOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Transaction
        </Button>
      </div>

      {/* KPI Cards */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as TransactionsImportTab)
          setPage(1)
        }}
      >
        {/* DCP-269: only the two implemented tabs are shown. The empty
            "coming soon" placeholder tabs (Account Import Status, Transactions
            categorization, API import, Import History) are hidden until built. */}
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="transactions-files-upload">
            Transactions files upload
          </TabsTrigger>
          <TabsTrigger value="transactions-manual-input">
            Transactions manual input
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transactions-files-upload" className="mt-6">
          <FileImportSection config={transactionImportConfig} />
        </TabsContent>

        <TabsContent value="transactions-manual-input" className="mt-6">
          <ManualTransactionsTable
            transactions={manualTransactions || []}
            loading={manualLoading}
            onClearFilters={clearManualFilters}
            onEdit={handleEdit}
            onDelete={handleDelete}
            page={page}
            pageSize={pageSize}
            totalCount={manualTotalCount}
            onPageChange={setPage}
          />
        </TabsContent>
      </Tabs>
      {/* Edit Transaction Dialog */}
      <ManualTransactionFormDialog
        open={isEditFormOpen}
        onOpenChange={handleEditFormClose}
        transaction={editingTransaction}
      />

      {/* Create Transactions Dialog (batch) */}
      <ManualTransactionBatchFormDialog
        open={isCreateFormOpen}
        onOpenChange={(open) => { if (!open) setIsCreateFormOpen(false) }}
      />

      {/* Delete transaction confirmation dialog */}
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setTxToDelete(null) }}
        title="Delete Transaction"
        description="This action cannot be undone"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteConfirmOpen(false); setTxToDelete(null) }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteTransaction.isPending}
            >
              {deleteTransaction.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this transaction?</p>
      </Modal>
    </div>
  )
}
